/**
 * USE RETAILER PAGES HOOK - Control Plane Sync V2
 * ================================================
 * 
 * SOURCE UNIQUE pour toute la navigation Retailer.
 * 100% piloté par le Control Plane (/api/page-settings).
 * 
 * RÈGLES:
 * - Visibilité: is_published_retailer === true (Control Plane)
 * - Ordre: display_order (Control Plane)
 * - Les pages RESERVED/MISSING/LEGACY sont toujours cachées
 * - Aucune dépendance à pageConfig ou Registry pour la navigation
 * 
 * SAFE MODE: Read-only, aucune modification de routes ou backend.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';
import { 
  PAGE_VISIBILITY_CONFIG, 
  PAGE_STATUS, 
  BUSINESS_CATEGORY 
} from '../lib/pageVisibilityEngine';
import { PAGE_TYPES } from '../lib/pageRegistry';
import { HUB_CONFIGS, LISTING_CONFIGS } from '../lib/pageConfig';

// Cache local pour éviter les appels répétés
let cachedSettings = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 secondes

// EventEmitter pour synchroniser les composants lors d'un refresh
const listeners = new Set();
export const onRetailerPagesUpdate = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};
const notifyListeners = () => {
  listeners.forEach(cb => cb());
};

/**
 * Hook principal - Toutes les pages Retailer filtrées et triées
 * SOURCE UNIQUE: Control Plane (/api/page-settings/retailer-visible)
 * 
 * STRATÉGIE SAFE MODE:
 * - Utilise endpoint public /api/page-settings/retailer-visible
 * - Récupère liste des page_ids non publiés
 * - Filtre côté frontend
 */
export const useRetailerPages = () => {
  const [pages, setPages] = useState([]);
  const [settingsMap, setSettingsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const loadPages = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // 1. Charger les unpublished page_ids depuis Control Plane (endpoint public)
      let unpublishedIds = [];
      const now = Date.now();
      
      if (!forceRefresh && cachedSettings && (now - cacheTimestamp) < CACHE_TTL) {
        unpublishedIds = cachedSettings;
      } else {
        try {
          const response = await api.get('/page-settings/retailer-visible');
          unpublishedIds = response.data?.unpublished_page_ids || [];
          cachedSettings = unpublishedIds;
          cacheTimestamp = now;
        } catch (err) {
          // Si erreur API, utiliser le cache ou continuer sans filtre
          console.warn('Could not load visibility from Control Plane:', err);
          unpublishedIds = cachedSettings || [];
        }
      }
      
      if (!mountedRef.current) return;
      
      // 2. Créer une map pour lookup rapide
      const unpublishedSet = new Set(unpublishedIds);
      setSettingsMap({ unpublished: unpublishedIds });
      
      // 3. Fusionner PAGE_VISIBILITY_CONFIG + Control Plane filter
      // RÈGLE: Control Plane filtre les pages non publiées
      const mergedPages = Object.entries(PAGE_VISIBILITY_CONFIG)
        .filter(([pageId, page]) => {
          // Filtre de base: uniquement les pages potentiellement visibles en retailer
          if (!page.visibility?.retailer) return false;
          
          // Exclure les pages avec statuts non-visibles
          if (page.status !== PAGE_STATUS.ACTIVE && 
              page.status !== PAGE_STATUS.CMS_ONLY) {
            return false;
          }
          
          // FILTRE CONTROL PLANE: exclure les pages non publiées
          if (unpublishedSet.has(pageId)) {
            return false;
          }
          
          return true;
        })
        .map(([pageId, page]) => {
          // Enrichir avec pageConfig pour la navigation HUB (read-only)
          let configData = {};
          const pageConfig = page.pageConfig || pageId;
          const hubConfig = HUB_CONFIGS?.[pageConfig];
          const listingConfig = LISTING_CONFIGS?.[pageConfig];
          const config = hubConfig || listingConfig;
          
          if (config) {
            configData = {
              configTitle: config.title,
              configSubtitle: config.subtitle,
              configIcon: config.icon,
              navigation: config.navigation || []
            };
          }
          
          return {
            id: pageId,
            ...page,
            ...configData,
            // Control Plane: cette page est publiée (pas dans la liste unpublished)
            is_published_retailer: true,
            display_order: page.order ?? 99,
            hub_cms_enabled: true,
            // Computed
            isVisible: true
          };
        })
        // TRI: par ordre statique (order du registry)
        .sort((a, b) => a.display_order - b.display_order);
      
      if (!mountedRef.current) return;
      
      setPages(mergedPages);
      setError(null);
    } catch (err) {
      console.error('Error loading retailer pages from Control Plane:', err);
      if (!mountedRef.current) return;
      
      setError(err.message);
      
      // Fallback: utiliser les pages du Registry sans Control Plane (mode dégradé)
      const fallbackPages = Object.entries(PAGE_VISIBILITY_CONFIG)
        .filter(([, page]) => page.visibility?.retailer && page.status === PAGE_STATUS.ACTIVE)
        .map(([pageId, page]) => ({
          id: pageId,
          ...page,
          is_published_retailer: true,
          display_order: page.order || 99,
          isVisible: true
        }))
        .sort((a, b) => a.display_order - b.display_order);
      
      setPages(fallbackPages);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Reset mounted ref on mount
    mountedRef.current = true;
    
    loadPages();
    
    // S'abonner aux updates (quand un autre composant invalide le cache)
    const unsubscribe = onRetailerPagesUpdate(() => {
      loadPages(true); // Force refresh
    });
    
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [loadPages]);

  // === HELPERS ===
  
  // Pages HUB métier (pour le menu principal)
  const getHubPages = useMemo(() => 
    pages.filter(p => 
      p.type === PAGE_TYPES.HUB && 
      p.category === BUSINESS_CATEGORY.METIER
    ),
    [pages]
  );

  // Pages LISTING (sous-pages des HUB)
  const getListingPages = useMemo(() => 
    pages.filter(p => p.type === PAGE_TYPES.LISTING),
    [pages]
  );

  // Pages du menu principal (HUB + CMS métier)
  const getMenuPages = useMemo(() => 
    pages.filter(p => 
      (p.type === PAGE_TYPES.HUB || p.status === PAGE_STATUS.CMS_ONLY) &&
      p.category === BUSINESS_CATEGORY.METIER
    ),
    [pages]
  );
  
  // Pages CMS uniquement
  const getCmsPages = useMemo(() =>
    pages.filter(p => p.status === PAGE_STATUS.CMS_ONLY),
    [pages]
  );

  const getPageById = useCallback((pageId) => 
    pages.find(p => p.id === pageId),
    [pages]
  );

  const getPageByRoute = useCallback((route) => {
    const normalized = route?.toLowerCase().replace(/\/$/, '');
    return pages.find(p => p.route?.toLowerCase() === normalized);
  }, [pages]);

  const getChildrenOf = useCallback((parentId) => 
    pages.filter(p => p.parent === parentId),
    [pages]
  );

  const isPageVisible = useCallback((pageId) => {
    const page = pages.find(p => p.id === pageId);
    return page?.isVisible ?? false;
  }, [pages]);

  return {
    pages,
    loading,
    error,
    settingsMap,
    // Helpers
    hubPages: getHubPages,
    listingPages: getListingPages,
    menuPages: getMenuPages,
    cmsPages: getCmsPages,
    getPageById,
    getPageByRoute,
    getChildrenOf,
    isPageVisible,
    // Refresh (force reload from Control Plane)
    reload: () => loadPages(true)
  };
};

/**
 * Hook pour le menu principal Retailer (HUBs uniquement)
 */
export const useRetailerMenu = () => {
  const { menuPages, loading, error } = useRetailerPages();
  
  return {
    menuItems: menuPages,
    loading,
    error
  };
};

/**
 * Hook pour vérifier si une page est accessible
 * Utilisé pour la protection des routes
 */
export const usePageAccess = (pageId) => {
  const { isPageVisible, loading, getPageById } = useRetailerPages();
  
  const page = useMemo(() => getPageById(pageId), [getPageById, pageId]);
  const hasAccess = useMemo(() => isPageVisible(pageId), [isPageVisible, pageId]);
  
  return {
    hasAccess,
    page,
    loading,
    shouldRedirect: !loading && !hasAccess
  };
};

/**
 * Hook pour la navigation d'un HUB (ses enfants LISTING)
 */
export const useHubNavigation = (hubId) => {
  const { getChildrenOf, getPageById, loading } = useRetailerPages();
  
  const hub = useMemo(() => getPageById(hubId), [getPageById, hubId]);
  const children = useMemo(() => getChildrenOf(hubId), [getChildrenOf, hubId]);
  
  // Navigation items enrichis avec données pageConfig
  const navigationItems = useMemo(() => {
    if (!hub?.navigation) return children;
    
    // Utiliser la navigation de pageConfig si disponible, filtrée par visibilité
    return hub.navigation.filter(navItem => {
      const childPage = children.find(c => c.route === navItem.link);
      return childPage?.isVisible !== false;
    });
  }, [hub, children]);
  
  return {
    hub,
    children,
    navigationItems,
    loading,
    hasNavigation: navigationItems.length > 0
  };
};

/**
 * Invalider le cache (après modification admin)
 * Notifie tous les composants abonnés
 */
export const invalidateRetailerPagesCache = () => {
  cachedSettings = null;
  cacheTimestamp = 0;
  // Notifier tous les composants pour refresh
  notifyListeners();
};

export default {
  useRetailerPages,
  useRetailerMenu,
  usePageAccess,
  useHubNavigation,
  invalidateRetailerPagesCache,
  onRetailerPagesUpdate
};
